import React, { useState, useMemo, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const Supports = () => {
  const [supports, setSupports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSupport, setSelectedSupport] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    department: '',
    role: '',
    status: 'Active'
  });

  const mockSupports = [
    {
      id: 'SUP001',
      name: 'Rahul Kumar',
      email: 'rahul.support@dms.com',
      mobile: '+91 9876543210',
      department: 'Technical Support',
      role: 'Senior Support Executive',
      status: 'Active',
      lastActive: '2024-01-15T10:30:00Z',
      createdAt: '2024-01-10T08:00:00Z'
    },
    {
      id: 'SUP002',
      name: 'Priya Sharma',
      email: 'priya.support@dms.com',
      mobile: '+91 9876543211',
      department: 'Customer Support',
      role: 'Support Manager',
      status: 'Active',
      lastActive: '2024-01-14T15:20:00Z',
      createdAt: '2024-01-05T10:00:00Z'
    },
    {
      id: 'SUP003',
      name: 'Amit Patel',
      email: 'amit.support@dms.com',
      mobile: '+91 9876543212',
      department: 'Sales Support',
      role: 'Support Executive',
      status: 'Inactive',
      lastActive: '2024-01-12T09:45:00Z',
      createdAt: '2024-01-01T12:00:00Z'
    }
  ];

  useEffect(() => {
    setSupports(mockSupports);
  }, []);

  const filteredData = useMemo(() => {
    let filtered = supports;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        Object.values(item).some(value =>
          value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    Object.keys(columnFilters).forEach(column => {
      const filterValue = columnFilters[column];
      if (filterValue) {
        filtered = filtered.filter(item =>
          item[column]?.toString().toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    });

    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    return filtered;
  }, [supports, searchQuery, columnFilters, dateRange]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setColumnFilters({});
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Supports');
    XLSX.writeFile(workbook, `supports_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedSupport(null);
    setModalMode('add');
    setFormData({
      name: '',
      email: '',
      mobile: '',
      department: '',
      role: '',
      status: 'Active'
    });
    setShowModal(true);
  };

  const handleEdit = (support) => {
    setSelectedSupport(support);
    setModalMode('edit');
    setFormData({
      name: support.name,
      email: support.email,
      mobile: support.mobile,
      department: support.department,
      role: support.role,
      status: support.status
    });
    setShowModal(true);
  };

  const handleDelete = (support) => {
    if (window.confirm(`Are you sure you want to delete ${support.name}?`)) {
      setSupports(prev => prev.filter(s => s.id !== support.id));
      toast.success(`${support.name} deleted successfully`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalMode === 'add') {
      const newSupport = {
        ...formData,
        id: `SUP${String(supports.length + 1).padStart(3, '0')}`,
        lastActive: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      setSupports(prev => [...prev, newSupport]);
      toast.success('Support member added successfully');
    } else {
      setSupports(prev => prev.map(s => 
        s.id === selectedSupport.id ? { ...s, ...formData } : s
      ));
      toast.success('Support member updated successfully');
    }
    
    setShowModal(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadge = (status) => {
    return status === 'Active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-headset me-2"></i>
          Support Team
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add Support
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search supports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={columnFilters.status || ''}
            onChange={(e) => handleColumnFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={columnFilters.department || ''}
            onChange={(e) => handleColumnFilterChange('department', e.target.value)}
          >
            <option value="">All Departments</option>
            <option value="Technical Support">Technical Support</option>
            <option value="Customer Support">Customer Support</option>
            <option value="Sales Support">Sales Support</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          />
        </Col>
        <Col md={2}>
          <Button variant="outline-secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Department</th>
              <th>Role</th>
              <th>Last Active</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((support) => (
              <tr key={support.id}>
                <td><strong>{support.id}</strong></td>
                <td>{support.name}</td>
                <td>{support.email}</td>
                <td>{support.mobile}</td>
                <td>{support.department}</td>
                <td>{support.role}</td>
                <td>{new Date(support.lastActive).toLocaleString()}</td>
                <td>{getStatusBadge(support.status)}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(support)}
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(support)}
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Support Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} Support Member</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="+91 9876543210"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    required
                  >
                    <option value="">Select Department</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Customer Support">Customer Support</option>
                    <option value="Sales Support">Sales Support</option>
                    <option value="Operations Support">Operations Support</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    placeholder="e.g., Support Executive"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'add' ? 'Add' : 'Update'} Support
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Supports;